import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Loader2, Image as ImageIcon, Coins, Search, AlertCircle, Info, Copy, Check } from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import { toast } from 'sonner';
import { 
  fetchMultiChainWalletAssets, 
  fetchMultiChainWalletNFTs, 
  type ChainId, 
  type MultiChainToken, 
  type MultiChainNFT 
} from '../lib/multiChainRPC';
import { getEnabledChains, getChainMetadata } from '../lib/chainRegistry';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

// IPFS gateway fallbacks for image loading
const IPFS_GATEWAYS = [
  'https://gateway.pinata.cloud/ipfs/',
  'https://nftstorage.link/ipfs/',
  'https://ipfs.filebase.io/ipfs/',
  'https://dweb.link/ipfs/',
  'https://ipfs.io/ipfs/',
  'https://cloudflare-ipfs.com/ipfs/',
];

// Try alternative IPFS gateway if image fails to load
const tryIPFSGateway = (img: HTMLImageElement, originalSrc: string, gatewayIndex: number = 0) => {
  if (gatewayIndex >= IPFS_GATEWAYS.length) {
    img.style.display = 'none';
    const parent = img.parentElement;
    if (parent && !parent.querySelector('.image-placeholder')) {
      const placeholder = document.createElement('div');
      placeholder.className = 'image-placeholder flex items-center justify-center h-full';
      placeholder.innerHTML = '<svg class="h-16 w-16 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>';
      parent.appendChild(placeholder);
    }
    return;
  }
  
  let ipfsHash: string | undefined;
  
  if (originalSrc.includes('/ipfs/')) {
    const parts = originalSrc.split('/ipfs/');
    if (parts.length > 1) {
      const hashAndPath = parts[1].split('?')[0].split('#')[0];
      const hashMatch = hashAndPath.match(/^([Qmb][a-zA-Z0-9]{43,})/);
      if (hashMatch) {
        ipfsHash = hashMatch[1];
      } else {
        ipfsHash = hashAndPath.split('/')[0];
      }
    }
  } else if (originalSrc.startsWith('ipfs://')) {
    ipfsHash = originalSrc.slice(7).split('/')[0].split('?')[0].split('#')[0];
  }
  
  if (ipfsHash) {
    const newSrc = `${IPFS_GATEWAYS[gatewayIndex]}${ipfsHash}`;
    img.onerror = null;
    img.src = newSrc;
    img.onerror = () => tryIPFSGateway(img, originalSrc, gatewayIndex + 1);
  }
};

// Smart address format detection
function detectAddressFormat(address: string): 'evm' | 'ravencoin' | 'solana' | 'icp' | 'bitcoin' | 'unknown' {
  if (/^0x[a-fA-F0-9]{40}$/.test(address)) return 'evm';
  if (/^R[a-zA-Z0-9]{33}$/.test(address)) return 'ravencoin';
  
  // ICP principals contain hyphens and end with specific patterns
  if (address.includes('-') && /^[a-z0-9]{5}-[a-z0-9]{5}-[a-z0-9]{5}-[a-z0-9]{5}-[a-z0-9]{3}$/.test(address)) {
    return 'icp';
  }
  
  // Solana addresses are base58 encoded, 32-44 chars, no hyphens
  if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address) && !address.startsWith('R') && !address.startsWith('0x') && !address.includes('-')) {
    return 'solana';
  }
  
  if (/^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(address)) return 'bitcoin';
  return 'unknown';
}

// Auto-select chains based on address format
function getRelevantChains(address: string): ChainId[] {
  const format = detectAddressFormat(address);
  const allChains = getEnabledChains();
  
  switch (format) {
    case 'evm':
      // Query all EVM chains
      return allChains.filter(c => c.type === 'EVM').map(c => c.id as ChainId);
    case 'ravencoin':
      return ['ravencoin'];
    case 'solana':
      return ['solana'];
    case 'icp':
      return ['icp'];
    default:
      // Unknown format - try all supported chains
      return allChains.map(c => c.id as ChainId);
  }
}

export default function UnifiedWalletViewer() {
  const [searchAddress, setSearchAddress] = useState('');
  const [activeAddress, setActiveAddress] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { walletAddress, isConnected } = useWallet();

  const [tokens, setTokens] = useState<MultiChainToken[]>([]);
  const [nfts, setNFTs] = useState<MultiChainNFT[]>([]);
  const [loading, setLoading] = useState(false);
  const [chainsSearched, setChainsSearched] = useState<ChainId[]>([]);

  // Group tokens and NFTs by chain
  const tokensByChain = tokens.reduce((acc, token) => {
    if (!acc[token.chain]) acc[token.chain] = [];
    acc[token.chain].push(token);
    return acc;
  }, {} as Record<ChainId, MultiChainToken[]>);

  const nftsByChain = nfts.reduce((acc, nft) => {
    if (!acc[nft.chain]) acc[nft.chain] = [];
    acc[nft.chain].push(nft);
    return acc;
  }, {} as Record<ChainId, MultiChainNFT[]>);

  // Get chains that actually have assets (filter out empty chains)
  const chainsWithAssets = Array.from(
    new Set([...Object.keys(tokensByChain), ...Object.keys(nftsByChain)])
  ) as ChainId[];

  const handleSearch = async () => {
    const address = searchAddress.trim();
    
    if (!address) {
      toast.error('Please enter a wallet address');
      return;
    }

    // Auto-detect relevant chains
    const relevantChains = getRelevantChains(address);
    const addressFormat = detectAddressFormat(address);
    
    if (addressFormat === 'unknown') {
      toast.error('Invalid address format', {
        description: 'Supported formats: 0x... (EVM), R... (Ravencoin), Solana, or ICP principal',
      });
      return;
    }

    setLoading(true);
    setActiveAddress(address);
    setChainsSearched(relevantChains);
    setTokens([]);
    setNFTs([]);

    try {
      const startTime = Date.now();
      toast.info(`⚡ Scanning ${relevantChains.length} chains...`);
      
      const [fetchedTokens, fetchedNFTs] = await Promise.all([
        fetchMultiChainWalletAssets(address, relevantChains),
        fetchMultiChainWalletNFTs(address, relevantChains),
      ]);

      const loadTime = Date.now() - startTime;

      setTokens(fetchedTokens);
      setNFTs(fetchedNFTs);

      const totalAssets = fetchedTokens.length + fetchedNFTs.length;

      if (totalAssets === 0) {
        toast.warning('No assets found', {
          description: `Searched ${relevantChains.length} chains but found no tokens or NFTs for this address.`,
        });
      } else {
        // Show if it was instant (cached) or fresh query
        const wasInstant = loadTime < 500;
        const chainsFound = Array.from(new Set([
          ...fetchedTokens.map(t => t.chain),
          ...fetchedNFTs.map(n => n.chain)
        ])).length;
        
        toast.success(wasInstant ? `⚡ INSTANT! Found ${totalAssets} assets` : `Found ${totalAssets} assets`, {
          description: wasInstant 
            ? `Cached data - ${totalAssets} assets on ${chainsFound} chains`
            : `Loaded in ${(loadTime / 1000).toFixed(1)}s - ${totalAssets} assets on ${chainsFound} chains. Next search will be instant!`,
        });
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to fetch wallet assets');
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = () => {
    if (activeAddress) {
      navigator.clipboard.writeText(activeAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Address copied to clipboard');
    }
  };

  const handleImageError = async (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const originalSrc = img.src;
    
    // Check if this is an IPFS URL
    if (originalSrc.includes('ipfs')) {
      // First try alternative IPFS gateways
      tryIPFSGateway(img, originalSrc, 0);
      
      // Also try to resolve JSON metadata if it's a JSON file
      try {
        const response = await fetch(originalSrc, { 
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        const contentType = response.headers.get('content-type') || '';
        
        if (contentType.includes('json') || contentType.includes('application/json')) {
          const jsonData = await response.json();
          const imageField = jsonData.image || jsonData.image_url || jsonData.imageUrl || jsonData.uri;
          
          if (imageField) {
            let imageUrl = imageField;
            
            // Handle IPFS protocol
            if (imageUrl.startsWith('ipfs://')) {
              const hash = imageUrl.replace('ipfs://', '').trim().split('/')[0];
              imageUrl = `https://dweb.link/ipfs/${hash}`;
            } else if (imageUrl.startsWith('Qm') || imageUrl.startsWith('bafy')) {
              imageUrl = `https://dweb.link/ipfs/${imageUrl}`;
            } else if (imageUrl.includes('/ipfs/')) {
              // Already has gateway, use as-is
            } else if (!imageUrl.startsWith('http')) {
              // Relative path or bare hash
              imageUrl = `https://dweb.link/ipfs/${imageUrl}`;
            }
            
            // Try the resolved image URL
            img.src = imageUrl;
            return;
          }
        }
      } catch (error) {
        // JSON fetch failed, continue with gateway fallback
      }
    } else {
      img.style.display = 'none';
    }
  };

  return (
    <Card className="w-full max-w-6xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="h-6 w-6" />
          Wallet Viewer
        </CardTitle>
        <CardDescription>
          View tokens and NFTs across all supported blockchains
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Search Section */}
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Wallet Address</span>
              </div>
              {/* Info button showing supported chains */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 gap-2">
                    <Info className="h-4 w-4" />
                    <span className="text-xs">Supported Chains</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>21 Chains Supported</DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="h-80">
                    <div className="space-y-1 pr-4">
                      {getEnabledChains().map((chain) => (
                        <div key={chain.id} className="flex items-center gap-2 text-sm py-2 border-b">
                          <div className={`h-2 w-2 rounded-full ${chain.color.split(' ')[0].replace('/10', '')}`} />
                          <span>{chain.displayName}</span>
                          <Badge variant="outline" className="ml-auto text-xs">
                            {chain.nativeSymbol}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  <div className="text-xs text-muted-foreground border-t pt-3">
                    💡 Chains are auto-detected from your address format
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Enter address (0x..., R..., Solana, or ICP principal)"
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
              />
              <Button onClick={handleSearch} disabled={loading || !searchAddress.trim()}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Search
                  </>
                )}
              </Button>
            </div>
            
            {/* Auto-fill from connected wallet */}
            {isConnected && walletAddress && !activeAddress && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setSearchAddress(walletAddress)}
              >
                Use Connected Wallet Address
              </Button>
            )}
          </div>

          {/* Active address display */}
          {activeAddress && (
            <div className="flex items-center gap-2 p-3 bg-accent/20 rounded-lg border border-border">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-foreground mb-1">Viewing Address</div>
                <div className="font-mono text-sm truncate">{activeAddress}</div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyAddress}
                className="shrink-0"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Results Section - Only show chains with assets */}
        {activeAddress && !loading && (
          <>
            {chainsWithAssets.length === 0 ? (
              // No assets found
              <div className="text-center py-12 space-y-4">
                <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
                <div className="space-y-2">
                  <div className="font-semibold text-lg">No Assets Found</div>
                  <div className="text-sm text-muted-foreground max-w-md mx-auto">
                    This wallet has no tokens or NFTs on any of the {chainsSearched.length} chains searched.
                  </div>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Info className="mr-2 h-4 w-4" />
                      View Chains Searched
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-sm">
                    <DialogHeader>
                      <DialogTitle>Chains Searched ({chainsSearched.length})</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-1">
                      {chainsSearched.map((chainId) => {
                        const meta = getChainMetadata(chainId);
                        return (
                          <div key={chainId} className="text-sm flex items-center gap-2 py-1">
                            <div className={`h-2 w-2 rounded-full ${meta?.color.split(' ')[0].replace('/10', '')}`} />
                            {meta?.displayName}
                          </div>
                        );
                      })}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            ) : (
              // Assets found - show by chain
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">
                    Found assets on {chainsWithAssets.length} {chainsWithAssets.length === 1 ? 'chain' : 'chains'}
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-xs">
                        <Info className="mr-1 h-3 w-3" />
                        Searched {chainsSearched.length} chains
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>All Chains Searched</DialogTitle>
                      </DialogHeader>
                      <div className="grid grid-cols-2 gap-2">
                        {chainsSearched.map((chainId) => {
                          const meta = getChainMetadata(chainId);
                          const hasAssets = chainsWithAssets.includes(chainId);
                          return (
                            <div key={chainId} className="text-sm flex items-center gap-2 p-2 rounded border">
                              <div className={`h-2 w-2 rounded-full ${meta?.color.split(' ')[0].replace('/10', '')} ${!hasAssets && 'opacity-30'}`} />
                              <span className={!hasAssets ? 'text-muted-foreground' : ''}>
                                {meta?.displayName}
                              </span>
                              {hasAssets && <span className="ml-auto text-green-500 text-xs">✓</span>}
                            </div>
                          );
                        })}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Display each chain's assets */}
                {chainsWithAssets.map((chainId) => {
                  const chainTokens = tokensByChain[chainId] || [];
                  const chainNFTs = nftsByChain[chainId] || [];
                  const chainMeta = getChainMetadata(chainId);

                  if (chainTokens.length === 0 && chainNFTs.length === 0) return null;

                  return (
                    <Card key={chainId} className={`border-l-4 ${chainMeta?.color || ''}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`h-3 w-3 rounded-full ${chainMeta?.color.split(' ')[0].replace('/10', '')}`} />
                            <CardTitle className="text-lg">{chainMeta?.displayName}</CardTitle>
                          </div>
                          <Badge variant="outline">
                            {chainTokens.length} tokens, {chainNFTs.length} NFTs
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Tabs defaultValue="tokens">
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="tokens">
                              Tokens ({chainTokens.length})
                            </TabsTrigger>
                            <TabsTrigger value="nfts">
                              NFTs ({chainNFTs.length})
                            </TabsTrigger>
                          </TabsList>

                          <TabsContent value="tokens" className="space-y-2 mt-4">
                            {chainTokens.length === 0 ? (
                              <div className="text-center py-8 text-sm text-muted-foreground">
                                No tokens on this chain
                              </div>
                            ) : (
                              <ScrollArea className="h-64">
                                <div className="space-y-2 pr-4">
                                  {chainTokens.map((token, idx) => (
                                    <div
                                      key={`${token.chain}-${token.contractAddress || token.symbol}-${idx}`}
                                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                                    >
                                      <div className="flex items-center gap-3">
                                        <Coins className="h-5 w-5 text-primary" />
                                        <div>
                                          <div className="font-semibold">{token.symbol}</div>
                                          <div className="text-xs text-muted-foreground">{token.name}</div>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="font-semibold">
                                          {token.balance.toLocaleString(undefined, {
                                            maximumFractionDigits: 6,
                                          })}
                                        </div>
                                        {token.contractAddress && (
                                          <div className="text-xs text-muted-foreground font-mono">
                                            {token.contractAddress.slice(0, 6)}...{token.contractAddress.slice(-4)}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </ScrollArea>
                            )}
                          </TabsContent>

                          <TabsContent value="nfts" className="space-y-2 mt-4">
                            {chainNFTs.length === 0 ? (
                              <div className="text-center py-8 text-sm text-muted-foreground">
                                No NFTs on this chain
                              </div>
                            ) : (
                              <ScrollArea className="h-96">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pr-4">
                                  {chainNFTs.map((nft, idx) => (
                                    <div
                                      key={`${nft.chain}-${nft.contractAddress}-${nft.tokenId}-${idx}`}
                                      className="group relative aspect-square rounded-lg border bg-card overflow-hidden hover:border-primary transition-colors"
                                    >
                                      {nft.imageUrl ? (
                                        <img
                                          src={nft.imageUrl}
                                          alt={nft.name || 'NFT'}
                                          className="w-full h-full object-cover"
                                          onError={handleImageError}
                                          loading="lazy"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-accent/50">
                                          <ImageIcon className="h-12 w-12 text-muted-foreground" />
                                        </div>
                                      )}
                                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                                        <div className="text-white text-xs font-semibold truncate">
                                          {nft.name || `#${nft.tokenId.slice(0, 8)}`}
                                        </div>
                                        {nft.collectionName && (
                                          <div className="text-white/70 text-[10px] truncate">
                                            {nft.collectionName}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </ScrollArea>
                            )}
                          </TabsContent>
                        </Tabs>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Loading state */}
        {loading && (
          <div className="text-center py-12 space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <div className="space-y-2">
              <div className="font-semibold">Scanning Blockchains...</div>
              <div className="text-sm text-muted-foreground">
                Querying {chainsSearched.length} chains for tokens and NFTs
              </div>
            </div>
          </div>
        )}

        {/* Empty state - before search */}
        {!activeAddress && !loading && (
          <div className="text-center py-12 space-y-4">
            <Coins className="h-12 w-12 text-muted-foreground mx-auto" />
            <div className="space-y-2">
              <div className="font-semibold text-lg">Enter a Wallet Address</div>
              <div className="text-sm text-muted-foreground max-w-md mx-auto">
                View tokens and NFTs across 21 blockchains. Supports Ethereum, Solana, ICP, Ravencoin, and more.
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

